import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import type { ApiResponse, PaginatedResponse, Pagination } from '@/app/types'
import api from '@/app/services/apiNir.ts'
import type { AxiosResponse } from 'axios'
import type {
    AffiliationAttachment,
    EntityType,
    Field,
    FieldLookup,
    Form, FormDatum, FormDefinition, FormStep,
} from '@/domains/home/services/nir/configurations/types/forms.ts'

export const useFormsStore = defineStore("forms", () => {
  const okResponse:Ref<object|undefined> = ref()
  const pagination:Ref<Pagination|undefined> = ref()
  const entityTypes:Ref<EntityType[]|undefined> = ref()
  const subEntityTypes:Ref<EntityType[]|undefined> = ref()
  const primaryEntityTypes:Ref<EntityType[]|undefined> = ref()
  const availableEntityTypes:Ref<EntityType[]|undefined> = ref()
  const institutionEntityTypes:Ref<EntityType[]|undefined> = ref()
  const fields:Ref<Field[]|undefined> = ref()
  const fieldLookups:Ref<FieldLookup[]|undefined> = ref()
  const forms:Ref<Form[]|undefined> = ref()
  const formSteps:Ref<FormStep[]|undefined> = ref()
  const form:Ref<Form|undefined> = ref()
  const formData:Ref<FormDatum|undefined> = ref()
  const formDefinition:Ref<FormDefinition|undefined> = ref()
  const affiliationAttachments:Ref<AffiliationAttachment[]|undefined> = ref()

  const createForm = async (payload:object) => {
    return api.post("/v1/configurations/forms", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const copyForm = async (payload:object) => {
    return api.post("/v1/configurations/forms/copy", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const updateForm = async (payload:object) => {
    return api.put("/v1/configurations/forms", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const createField = async (payload:object) => {
    return api.post("/v1/configurations/fields", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const updateValidationType = async (payload:object) => {
    return api.put("/v1/configurations/fields/validation", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const updateField = async (payload:object) => {
    return api.put("/v1/configurations/fields", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const removeField = async (payload: object) => {
    return api
      .put('/v1/configurations/fields/remove', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const createFieldLookup = async (payload:object) => {
    return api.post("/v1/configurations/field-lookups", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }


  const updateFieldLookup = async (payload:object) => {
    return api.put("/v1/configurations/field-lookups", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const fetchFields = async (page:number, limit:number) => {
    let path = `/v1/configurations/fields?page=${page}&limit=${limit}`
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<PaginatedResponse<Field>>>) => {
        fields.value = response.data.data.content
        pagination.value = response.data.data.pagination
      })
  }

  const fetchFieldLookups = async (id:string, page:number, limit:number) => {
    let path = `/v1/configurations/field-lookups?page=${page}&limit=${limit}`
    if (id != ""){
      path += `&fieldId=${id}`
    }
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<{data:FieldLookup[]}>>) => {
        fieldLookups.value = response.data.data.data
      })
  }

  const createEntityType = async (payload:object) => {
    return api.post("/v1/configurations/entity-types", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const updateEntityType = async (payload: object) => {
    return api
      .post('/v1/configurations/entity-types-update', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const deleteEntityType = async (id: string) => {
    return api
      .delete(`/v1/configurations/entity-types/${id}`)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const fetchEntityTypes = async (parentId:string) => {
    let path = `/v1/configurations/entity-types`
    if(parentId){
      path += `?parentId=${parentId}`
    }
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<{data:EntityType[]}>>) => {
        entityTypes.value = response.data.data.data
      })
  }

  const fetchSubEntityTypes = async (parentId:string) => {
    let path = `/v1/configurations/entity-types`
    if(parentId){
      path += `?parentId=${parentId}`
    }
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<{data:EntityType[]}>>) => {
        subEntityTypes.value = response.data.data.data
      })
  }

    const fetchAvailableEntityTypes = async () => {
        return api.get(`/v1/configurations/available-types`)
            .then((response: AxiosResponse<ApiResponse<{data:EntityType[]}>>) => {
                availableEntityTypes.value = response.data.data.data
            })
    }

  const fetchPrimaryEntityTypes = async () => {
    return api.get(`/v1/configurations/entity-types/primary`)
      .then((response: AxiosResponse<ApiResponse<{data:EntityType[]}>>) => {
        primaryEntityTypes.value = response.data.data.data
      })
  }

  const fetchInstitutionEntityTypes = async (id:string) => {
    return api.get(`/v1/configurations/institution/types?id=${id}`)
        .then((response: AxiosResponse<ApiResponse<{data:EntityType[]}>>) => {
          institutionEntityTypes.value = response.data.data.data
        })
  }

  const fetchPrimaryInstitutionEntityTypes = async (id:string) => {
    return api.get(`/v1/configurations/institution/types/primary?id=${id}`)
        .then((response: AxiosResponse<ApiResponse<{data:EntityType[]}>>) => {
          institutionEntityTypes.value = response.data.data.data
        })
  }

  const fetchForms = async (name:string, institutionId: string, purpose:string, page:number, limit:number) => {
    let path = `/v1/configurations/forms?page=${page}&limit=${limit}`
    if (institutionId != ""){
      path += `&institutionId=${institutionId}`
    }
    if (purpose!= ""){
        path += `&purpose=${purpose}`
    }
    if (name != ""){
        path += `&name=${name}`
    }
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<PaginatedResponse<Form>>>) => {
        forms.value = response.data.data.content
        pagination.value = response.data.data.pagination
      })
  }

  const fetchPublishedForms = async (institutionId: string, page:number, limit:number) => {
    let path = `/v1/configurations/published/forms?page=${page}&limit=${limit}`
    if (institutionId != ""){
      path += `&institutionId=${institutionId}`
    }
    return api.get(path)
        .then((response: AxiosResponse<ApiResponse<PaginatedResponse<Form>>>) => {
          forms.value = response.data.data.content
          pagination.value = response.data.data.pagination
        })
  }

  const createFormStep = async (payload:object) => {
    return api.post("/v1/configurations/forms/steps", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const createFormSection = async (payload:object) => {
    return api.post("/v1/configurations/forms/sections", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

    const updateFormSection = async (payload:object) => {
        return api.put("/v1/configurations/forms/sections", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

  const createFormField = async (payload:object) => {
    return api.post("/v1/configurations/forms/fields", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const updateFormField = async (payload:object) => {
    return api.put("/v1/configurations/forms/fields", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const createFormFieldRestriction = async (payload:object) => {
    return api.post("/v1/configurations/fields/restrictions", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const updateFormFieldRestriction = async (payload:object) => {
    return api.put("/v1/configurations/fields/restrictions", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const removeFormField = async (payload: object) => {
    return api
      .put('/v1/configurations/forms/fields/remove', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

    const removeFormSection = async (payload: object) => {
        return api
            .put('/v1/configurations/forms/sections/remove', payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

  const createFormAttachment = async (payload: object) => {
    return api.post("/v1/configurations/forms/attachments", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const updateFormAttachment = async (payload: object) => {
    return api.put("/v1/configurations/forms/attachments", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const removeFormAttachment = async (payload: object) => {
    return api
      .put('/v1/configurations/forms/attachments/remove', payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

    const fetchAffiliationAttachments = async (institutionId: string) => {
        return api.get(`/v1/configurations/affiliations/attachments?id=${institutionId}`)
            .then((response: AxiosResponse<ApiResponse<AffiliationAttachment[]>>) => {
                affiliationAttachments.value = response.data.data
            })
    }

    const createAffiliationFormAttachment = async (payload: object) => {
        return api.post("/v1/configurations/affiliations/attachments", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const updateAffiliationFormAttachment = async (payload: object) => {
        return api.put("/v1/configurations/affiliations/attachments", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const removeAffiliationFormAttachment = async (payload: object) => {
        return api
            .put('/v1/configurations/affiliations/attachments/remove', payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

  const approve = async (payload: object) => {
    return api
        .put('/v1/configurations/forms/approve', payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const publish = async (payload: object) => {
    return api
        .put('/v1/configurations/forms/publish', payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const fetchFormFields = async (id:string) => {
    let path = `/v1/configurations/forms/fields?formId=${id}`
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<{data:FormDatum}>>) => {
        formData.value = response.data.data.data
      })
  }

    const fetchFormDefinition = async (id:string) => {
        let path = `/v1/configurations/forms/definition?formId=${id}`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<{data:FormDefinition}>>) => {
                formDefinition.value = response.data.data.data
            })
    }

  const fetchForm = async (id:string) => {
    let path = `/v1/configurations/form?id=${id}`
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<{data:Form}>>) => {
        form.value = response.data.data.data
      })
  }

  const moveField = async (payload:object) => {
    return api.put(`/v1/configurations/forms/fields/move`, payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const moveSection = async (payload:object) => {
    return api.put(`/v1/configurations/forms/sections/move`, payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const moveStep = async (payload:object) => {
    return api.put(`/v1/configurations/forms/steps/move`, payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const removeStep = async (payload:object) => {
    return api.put(`/v1/configurations/forms/steps/remove`, payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const assignSectionToStep = async (payload:object) => {
    return api.put(`/v1/configurations/forms/steps/assign-section`, payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const fetchFormSteps = async (id:string) => {
    let path = `/v1/configurations/form/steps?id=${id}`
    return api.get(path)
        .then((response: AxiosResponse<ApiResponse<{data:FormStep[]}>>) => {
          formSteps.value = response.data.data.data
        })
  }

    const enableDirectAdmission = async (payload:object) => {
        return api.put(`/v1/configurations/form/direct-admission/enable`, payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const disableDirectAdmission = async (payload:object) => {
        return api.put(`/v1/configurations/form/direct-admission/disable`, payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data;
            })
    }

    const enableInterimErn = async (payload: object) => {
        return api.put(`/v1/configurations/form/interim-ern/enable`, payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data;
            });
    };

    const disableInterimErn = async (payload: object) => {
        return api.put(`/v1/configurations/form/interim-ern/disable`, payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

  const addRegulationFormTemplate = async (payload: object) => {
    return api.post("/v1/configurations/regulation-forms/templates", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const updateRegulationFormTemplate = async (payload: object) => {
    return api.put("/v1/configurations/regulation-forms/templates", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  return {

    okResponse,
    fields,
    entityTypes,
    subEntityTypes,
    fieldLookups,
    forms,
    form,
    formData,
    formSteps,
    primaryEntityTypes,
    institutionEntityTypes,
      availableEntityTypes,
      formDefinition,
    pagination,
    createField,
    fetchFields,
    removeField,
    createFieldLookup,
    fetchFieldLookups,
    updateField,
    updateFieldLookup,
    createForm,
    copyForm,
    updateForm,
    fetchFormFields,
    removeFormField,
    createEntityType,
    deleteEntityType,
    fetchEntityTypes,
    fetchForms,
    fetchPublishedForms,
    createFormSection,
    createFormField,
    fetchForm,
    fetchPrimaryEntityTypes,
    fetchInstitutionEntityTypes,
    fetchPrimaryInstitutionEntityTypes,
    fetchSubEntityTypes,
    updateEntityType,
    approve,
    publish,
    updateValidationType,
    createFormAttachment,
    removeFormAttachment,
    updateFormAttachment,
    moveField,
    moveSection,
    updateFormField,
    createFormFieldRestriction,
    updateFormFieldRestriction,
    createFormStep,
    moveStep,
    fetchFormSteps,
    removeStep,
    assignSectionToStep,
      fetchFormDefinition,
      removeFormSection,
      updateFormSection,
      enableDirectAdmission,
      disableDirectAdmission,
      enableInterimErn,
      disableInterimErn,
      fetchAvailableEntityTypes,
      affiliationAttachments,
      fetchAffiliationAttachments,
      createAffiliationFormAttachment,
      updateAffiliationFormAttachment,
      removeAffiliationFormAttachment,
      addRegulationFormTemplate,
      updateRegulationFormTemplate
  }
})